import { FC, useEffect, useState } from 'react';

import {
    Divider,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Skeleton,
    Stack,
    TextField,
    Typography,
    useTheme
} from '@mui/material';
import { Simulation, defaultSimulation } from 'models/Simulation';
import { useTranslation } from 'react-i18next';
import SimulationService from 'services/SimulationService';
import useAuth from 'hooks/useAuth';
import { IconAlertTriangle } from '@tabler/icons';

interface Error {
    field: string;
    message: string;
}

interface DetailsFormProps {
    createType: number;
    error: Error | null;
    newSimulation: Simulation;
    onChange: (simulation: Simulation) => void;
}

const DetailsForm: FC<DetailsFormProps> = (props) => {
    const { createType, error, newSimulation, onChange } = props;
    const { user } = useAuth();
    const theme = useTheme();
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [simulationsItems, setSimulationsItems] = useState<Simulation[]>([]);

    const handleSelect = (simulation: Simulation) => {
        const simulationCopy: Simulation = {
            ...newSimulation,
            simulationIdToCopy: simulation.id,
            name: `${simulation.name} Copy`,
            note: simulation.note
        };
        onChange(simulationCopy);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        const simulation: Simulation = {
            ...newSimulation,
            [name]: value
        };
        onChange(simulation);
    };

    const simulationService = new SimulationService();

    useEffect(() => {
        (async () => {
            if (createType === 1 && user) {
                try {
                    setIsLoading(true);
                    const res = await simulationService.getSimulationsItems(user.tenantId);
                    setSimulationsItems(res);
                } catch (error) {
                    console.log(error);
                }
                setIsLoading(false);
            }
        })();
    }, [createType, user]);

    return (
        <Stack direction="column" spacing={3}>
            <Typography variant="body1" component="span">
                Inserisci i dettagli della simulazione
            </Typography>
            {createType === 1 && (
                <>
                    {isLoading ? (
                        <Skeleton variant="rectangular" animation="wave" height={45} width="100%" />
                    ) : (
                        <FormControl sx={{ m: '0 0 16px 0', minWidth: 200 }} size="small">
                            <InputLabel id="simulation-select-label">Simulazione di partenza</InputLabel>
                            <Select
                                disabled={simulationsItems.length <= 0}
                                labelId="simulation-select-label"
                                id="simulation-select-label"
                                label="Simulazione di partenza"
                            >
                                {simulationsItems.map((simulationItem) => (
                                    <MenuItem
                                        key={simulationItem.id}
                                        value={simulationItem.id}
                                        onClick={() => handleSelect(simulationItem)}
                                    >
                                        {simulationItem.name}
                                    </MenuItem>
                                ))}
                            </Select>

                            {simulationsItems.length <= 0 && (
                                <FormHelperText sx={{ color: theme.palette.warning.dark }} id="simulation-select-helpertext">
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <IconAlertTriangle stroke={1} size="20px" />
                                        <Typography variant="subtitle2" color="inherit">
                                            {t('NON_CI_SONO_SIMULAZIONI_ATTIVE')}
                                        </Typography>
                                    </Stack>
                                </FormHelperText>
                            )}
                        </FormControl>
                    )}

                    <Divider />
                </>
            )}
            <TextField
                autoFocus
                required
                margin="dense"
                id="name"
                name="name"
                label="Nome"
                fullWidth
                variant="outlined"
                autoComplete="off"
                value={newSimulation.name}
                error={!!error && Boolean(error.field)}
                helperText={error && error.message && t(error.message)}
                onChange={handleChange}
            />
            <TextField
                multiline
                margin="dense"
                id="note"
                name="note"
                label="Note"
                fullWidth
                variant="outlined"
                autoComplete="off"
                value={newSimulation.note}
                onChange={handleChange}
            />
        </Stack>
    );
};

export default DetailsForm;
